import React from "react";

interface CardProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  variant?: "default" | "subtle";
  padded?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Surface with optional header/footer. The default variant is a bordered
 * card on the main background. The "subtle" variant uses bg-subtle for
 * nested cards or sidebar surfaces.
 */
export const Card: React.FC<CardProps> = ({
  header,
  footer,
  children,
  variant = "default",
  padded = true,
  className,
  style,
}) => {
  const bg =
    variant === "subtle" ? "var(--color-bg-subtle)" : "var(--color-bg)";
  const bodyPadding = padded ? "var(--spacing-md)" : undefined;

  return (
    <div
      className={className}
      style={{
        background: bg,
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-app)",
        overflow: "hidden",
        ...style,
      }}
    >
      {header && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderBottom: "1px solid var(--color-border)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          {header}
        </div>
      )}
      <div style={{ padding: bodyPadding }}>{children}</div>
      {footer && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderTop: "1px solid var(--color-border)",
            color: "var(--color-fg-muted)",
            fontSize: "var(--font-size-small)",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};
